import { useEffect, useState } from "react";

import { useJDSettings, useSaveJDSettings } from "@/api/jd-queries";
import { Button } from "@/components/ui/button";
import { Input, Select } from "@/components/ui/input";
import { Callout, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";

/**
 * Job-description settings — DESIGN §20.4. The `jd_*` rows of system_configs, edited as the text they
 * are stored as; the API refuses a value that would not parse, so what is saved is what is enforced.
 * The next upload and the next pick read them, with no deploy.
 */

const ROWS: { key: string; label: string; hint: string }[] = [
  { key: "jd_importance_weights", label: "Weight of each importance", hint: "must=3, nice=2, other=1" },
  { key: "jd_experience_basis", label: "Difficulty when a member picks a JD", hint: "" },
  { key: "jd_show_client_default", label: "Show the client's name on new JDs", hint: "" },
  { key: "jd_upload_types", label: "File types accepted", hint: "pdf, docx, txt" },
  { key: "jd_upload_max_mb", label: "Largest file, in MB", hint: "1-20" },
  { key: "jd_default_scope", label: "Where an approved JD question goes", hint: "" },
  { key: "jd_rm_self_approve", label: "Resource Managers approve their JD's questions", hint: "" },
  { key: "jd_match_min_score", label: "How close a matched bank question must be", hint: "0.50-0.99" },
  { key: "jd_match_max_per_skill", label: "Most questions matched to one skill", hint: "1-50" },
];

// Rows edited as a choice rather than as text: key -> [stored value, what it means].
const CHOICES: Record<string, [string, string][]> = {
  jd_experience_basis: [
    ["jd_min", "The JD's minimum experience"],
    ["member", "The member's own"],
    ["higher", "Whichever is higher"],
  ],
  jd_show_client_default: [
    ["no", "No"],
    ["yes", "Yes"],
  ],
  jd_default_scope: [
    ["jd_only", "This JD only"],
    ["bank", "The bank"],
  ],
  jd_rm_self_approve: [
    ["no", "No — a Maintainer approves"],
    ["yes", "Yes, to their JD only"],
  ],
};

export function JDSettingsPanel() {
  const settings = useJDSettings();
  const save = useSaveJDSettings();
  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settings.data) setDraft(settings.data.values);
  }, [settings.data]);

  const changed = Object.fromEntries(
    Object.entries(draft).filter(([key, value]) => value !== settings.data?.values[key]),
  );

  return (
    <Panel>
      <PanelHeader
        title="Job descriptions"
        actions={
          <Button
            size="sm"
            variant="primary"
            disabled={Object.keys(changed).length === 0 || save.isPending}
            onClick={() => save.mutate(changed)}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        }
      />
      <div className="divide-bank-border divide-y">
        {ROWS.map(({ key, label, hint }) => (
          <div key={key} className="grid items-center gap-3 px-4 py-2.5 sm:grid-cols-[minmax(0,1fr)_16rem]">
            <div>
              <p className="text-[13.5px] font-medium">{label}</p>
              <p className="text-bank-muted text-xs">{settings.data?.notes[key]}</p>
              <p className="text-bank-muted font-mono text-[11px]">{key}</p>
            </div>
            {CHOICES[key] ? (
              <Select
                aria-label={label}
                value={draft[key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              >
                {CHOICES[key].map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                aria-label={label}
                className="font-mono"
                placeholder={hint}
                value={draft[key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
              />
            )}
          </div>
        ))}
      </div>
      <div className="space-y-2 p-4 pt-2">
        {settings.data?.problems.map((p) => (
          <Callout key={p}>{p}</Callout>
        ))}
        <ErrorNote error={settings.error ?? save.error} />
        <p className="text-bank-muted text-xs">
          Who is a Resource Manager is set on Access, beside Admin, Maintainer and Viewer. How much of each
          round comes from a JD's own questions is its pick policy, below.
        </p>
      </div>
    </Panel>
  );
}
